import { lazy, Suspense, useEffect, useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Alert, AlertDescription } from '@quebon/ui/components/alert'
import { Button } from '@quebon/ui/components/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@quebon/ui/components/card'
import { Skeleton } from '@quebon/ui/components/skeleton'
import { ToggleGroup, ToggleGroupItem } from '@quebon/ui/components/toggle-group'
import { LmsPrintPreviewPdfButton, openLmsPrintPdfPreview } from '@/components/admin/print/LmsPrintPreviewPdfButton'
import type { ReviewNoteItemView, ReviewNoteSourceMode, ReviewNoteSourceLessonView } from '@/api/lcReviewNote'
import {
  useCancelReviewNote,
  useCompleteReviewNote,
  useCreateReviewNote,
  useExcludeReviewNoteItem,
  useGradeReviewNoteItem,
  useLockReviewNoteForPrint,
  useReviewNote,
  useReviewNoteDetail,
  useReviewNoteScope,
  useReviewNotePreview,
  useReviewNoteSourceLessons,
  reviewNoteSourceKeys,
} from '@/hooks/coaching/useReviewNote'
import { canExcludeReviewNoteItems, filterSelectableSourceLessonIds, isReviewNotePreviewForSelection, normalizeSourceLessonIds } from '@/utils/reviewNoteSources'
import { getApiErrorMessage } from '@/utils/apiError'
import { gradingDraftScope, useGradingDraft } from '@/hooks/coaching/useGradingDraft'
import type { CoachingGradingMode } from '@/hooks/coaching/usePersistedGradingMode'
import { CoachingOperationPanel } from './CoachingOperationPanel'
import { ReviewNoteRangePicker } from './ReviewNoteRangePicker'
import { ReviewNoteExcludeButton } from './ReviewNoteExcludeButton'
import { ReviewNoteExclusionList } from './ReviewNoteExclusionList'
import { ReviewNoteSourceSummary } from './ReviewNoteSourceSummary'
import { ReviewNoteSourceIcon } from './ReviewNoteSourceIcon'
import { GradingModeSwitch } from './GradingModeSwitch'
import { ConfirmDialog } from './ConfirmDialog'
import { parseStoredAnswer } from './component-details/parts/answerArray'
import { QuestionGradingList, type QuestionGradingItem } from './QuestionGradingList'
import { ReviewNoteGradingSheet } from './grading-sheet/ReviewNoteGradingSheet'
import type { QuizFocusRequest } from './grading-sheet/coachingFocus'

const LocalAnswerPreview = import.meta.env.DEV
  ? lazy(() => import('./grading-sheet/LocalAnswerPreview'))
  : null

interface Props {
  learnerId: number
  lessonId: number
  learningAttemptId: number | null
  hasAttemptScope: boolean
  readOnly?: boolean
  preventFinalization?: boolean
  preventSetSwitch?: boolean
  /** 채점/코칭 모드. 기본 coaching(한 문항씩 탐색). grading이면 문항 목록을 채점표로 렌더한다. */
  onGradingModeChange?: (mode: CoachingGradingMode) => void
  gradingMode?: CoachingGradingMode
  onRequestCoaching?: (quizId: string) => void
  focusRequest?: QuizFocusRequest | null
}

const SOURCE_MODES: ReviewNoteSourceMode[] = ['ALL', 'ESSENTIAL', 'WRONG']
const OPEN_STATUSES = new Set(['EMPTY', 'GENERATED', 'IN_PROGRESS'])
function toQuestionGradingItem(item: ReviewNoteItemView): QuestionGradingItem {
  return {
    quizId: item.quizId,
    gradedIsCorrect: item.gradedIsCorrect,
    studentAnswer: parseStoredAnswer(item.answer),
    corrects: item.corrects,
    questionSnapshot: item.questionSnapshot,
    correctAnswer: item.correctAnswer,
    explanation: item.explanation,
  }
}

export function ReviewNotePanel(props: Props) {
  return <ReviewNotePanelContent key={`${props.learnerId}:${props.lessonId}:${props.learningAttemptId ?? 'progress'}:${props.hasAttemptScope}:${props.readOnly === true}`} {...props} />
}

function ReviewNotePanelContent({
  learnerId,
  lessonId,
  learningAttemptId,
  hasAttemptScope,
  readOnly = false,
  preventFinalization = false,
  preventSetSwitch = false,
  gradingMode = 'coaching',
  onGradingModeChange,
  onRequestCoaching,
  focusRequest = null,
}: Props) {
  const { t } = useTranslation('coaching')
  const queryClient = useQueryClient()
  const sourcePickerTitleId = useId()
  const [selectedSource, setSelectedSource] = useState<ReviewNoteSourceMode | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [createWithExclusion, setCreateWithExclusion] = useState(false)
  const [exclusionSetId, setExclusionSetId] = useState<number | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [selectedLessonIds, setSelectedLessonIds] = useState<number[]>([lessonId])
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => { mounted.current = false }
  }, [])
  const [cancellation, setCancellation] = useState<{ setId: number; reason: string } | null>(null)
  const [requestedSetId, setRequestedSetId] = useState<number | null>(null)
  const summary = useReviewNote(learnerId, lessonId, learningAttemptId, hasAttemptScope)
  const scope = useReviewNoteScope(learnerId, lessonId, learningAttemptId, hasAttemptScope)
  const create = useCreateReviewNote(learnerId, lessonId)
  const exclude = useExcludeReviewNoteItem(learnerId)
  const summaryReviewNote = summary.data
  const scopeSets = scope.data?.sets ?? []
  const reviewNotes = scopeSets.length > 0
    ? scopeSets
    : (summaryReviewNote == null ? [] : [summaryReviewNote])
  const visibleReviewNotes = reviewNotes.filter((set) => set.status !== 'CANCELLED')
  const openReviewNote = visibleReviewNotes.find((set) => OPEN_STATUSES.has(set.status)) ?? null
  const defaultSetId = openReviewNote?.setId ?? visibleReviewNotes.at(-1)?.setId ?? null
  const selectedSetId = visibleReviewNotes.some((set) => set.setId === requestedSetId)
    ? requestedSetId
    : defaultSetId
  const activeCancellation = cancellation?.setId === selectedSetId ? cancellation : null
  const showCancel = activeCancellation != null
  const cancelReason = activeCancellation?.reason ?? ''
  const { draft: gradingDraft } = useGradingDraft(gradingDraftScope.reviewNote(selectedSetId ?? 0))
  const hasGradingDraft = Object.keys(gradingDraft).length > 0
  const selectedSummary = visibleReviewNotes.find((set) => set.setId === selectedSetId) ?? null
  const detail = useReviewNoteDetail(learnerId, selectedSetId)
  const grade = useGradeReviewNoteItem(learnerId, selectedSetId ?? 0)
  const lockForPrint = useLockReviewNoteForPrint(learnerId, selectedSetId ?? 0)
  const complete = useCompleteReviewNote(
    learnerId,
    lessonId,
    selectedSetId ?? 0,
    learningAttemptId,
  )
  const cancel = useCancelReviewNote(
    learnerId,
    lessonId,
    selectedSetId ?? 0,
    learningAttemptId,
  )
  const actionError = create.error
    ?? exclude.error
    ?? grade.error
    ?? complete.error
    ?? cancel.error
    ?? lockForPrint.error

  const creationClosed = scope.data?.creationClosed === true
  const usedSources = new Set(
    visibleReviewNotes.map((set) => set.sourceMode),
  )
  const canCreate = hasAttemptScope && !readOnly && summary.isSuccess && scope.isSuccess &&
    openReviewNote == null && !creationClosed
  const showCreate = !readOnly &&
    openReviewNote == null &&
    !creationClosed &&
    usedSources.size < SOURCE_MODES.length
  const activeReviewNote = detail.data ?? selectedSummary
  const canComplete = activeReviewNote != null &&
    !preventFinalization && activeReviewNote.totalCount > 0 &&
    activeReviewNote.gradedCount === activeReviewNote.totalCount
  const sourceLessons = useReviewNoteSourceLessons(learnerId, lessonId, learningAttemptId, canCreate)
  const selectableSourceLessonIds = sourceLessons.data == null
    ? selectedLessonIds
    : filterSelectableSourceLessonIds(selectedLessonIds, sourceLessons.data)
  const preview = useReviewNotePreview(
    learnerId, lessonId, learningAttemptId, selectableSourceLessonIds,
    canCreate && sourceLessons.isSuccess,
  )
  const previewReady = sourceLessons.isSuccess && !sourceLessons.isFetching &&
    preview.isSuccess && !preview.isFetching && isReviewNotePreviewForSelection(preview.data, selectableSourceLessonIds)
  const selectedQuestionCount = selectedSource == null ? undefined : preview.data?.counts[selectedSource]
  const selectedSourceAvailable = selectedSource != null && !usedSources.has(selectedSource) &&
    previewReady && selectedQuestionCount != null && selectedQuestionCount > 0
  const canSubmit = canCreate && selectedSourceAvailable && !create.isPending && !confirming
  const canExclude = !readOnly && activeReviewNote?.canEditItems === true && activeReviewNote.items.length > 0
  const exclusionExpanded = canExclude && canExcludeReviewNoteItems(activeReviewNote) && exclusionSetId === activeReviewNote.setId
  const hideGrading = exclusionExpanded && !hasGradingDraft && !preventFinalization && !preventSetSwitch
  const exclusionDisabled = exclude.isPending || grade.isPending || lockForPrint.isPending ||
    complete.isPending || cancel.isPending || create.isPending || showCancel || hasGradingDraft || preventFinalization || preventSetSwitch
  const excludeItem = (item: ReviewNoteItemView) => {
    if (!canExclude || exclusionDisabled || activeReviewNote == null || activeReviewNote.totalCount <= 1 || activeReviewNote.items.length <= 1) return
    exclude.mutate({ setId: activeReviewNote.setId, itemId: item.itemId, quizId: item.quizId })
  }

  if (summary.isLoading || scope.isLoading) {
    return <CoachingOperationPanel kind="reviewNote" title={t('reviewNote.title')}><p role="status" className="p-6 text-sm text-muted-foreground">{t('reviewNote.loading')}</p></CoachingOperationPanel>
  }
  if (summary.error || scope.error) {
    return <CoachingOperationPanel kind="reviewNote" title={t('reviewNote.title')}><Alert variant="destructive"><AlertDescription>{t('reviewNote.loadError')}</AlertDescription></Alert></CoachingOperationPanel>
  }


  return (
    <CoachingOperationPanel
      kind="reviewNote"
      title={t('reviewNote.title')}
      actions={onGradingModeChange ? <GradingModeSwitch mode={gradingMode} onChange={onGradingModeChange} /> : undefined}
    >
      {actionError ? (
        <p role="alert" className="border-b border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
          {getApiErrorMessage(actionError, t)}
        </p>
      ) : null}

      {visibleReviewNotes.length > 0 ? (
        <div className="flex flex-wrap gap-2 border-b border-gray-100 px-4 py-3" aria-label={t('reviewNote.setList')}>
          {visibleReviewNotes.map((set) => (
            <button
              key={set.setId}
              type="button"
              aria-pressed={selectedSetId === set.setId}
              disabled={preventSetSwitch || exclude.isPending}
              onClick={() => {
                setRequestedSetId(set.setId)
                setExclusionSetId(null)
                setCancellation(null)
              }}
              className="rounded-md border border-gray-200 px-3 py-2 text-left text-xs text-gray-600 aria-pressed:border-primary-600 aria-pressed:bg-primary-50 aria-pressed:text-primary-800"
            >
              <span className="block font-semibold">{t(`reviewNote.source.${set.sourceMode}`)}</span>
              <span className="mt-0.5 block">
                {t(`reviewNote.setStatus.${set.status}`, {
                  graded: set.gradedCount,
                  total: set.totalCount,
                })}
              </span>
            </button>
          ))}
        </div>
      ) : null}

      {creationClosed ? (
        <p role="status" className="border-b border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          {t('reviewNote.creationClosed')}
        </p>
      ) : null}

      {showCreate ? (
        <div className="flex flex-col gap-4 rounded-lg bg-muted/30 p-4">
          {!hasAttemptScope ? (
            <Alert role="status"><AlertDescription>{t('reviewNote.reviewNotStarted')}</AlertDescription></Alert>
          ) : null}
          {hasAttemptScope && sourceLessons.isLoading ? (
            <div role="status" className="flex flex-col gap-2">
              <p className="text-sm text-muted-foreground">{t('reviewNote.sourceLoading')}</p>
              <Skeleton className="h-28 w-full" />
            </div>
          ) : null}
          {sourceLessons.error ? (
            <Alert variant="destructive">
              <AlertDescription>{t('reviewNote.sourceError')}</AlertDescription>
              <Button variant="outline" size="sm" onClick={() => { void sourceLessons.refetch() }}>{t('reviewNote.retry')}</Button>
            </Alert>
          ) : null}
          {sourceLessons.data ? (
            <ReviewNoteRangePicker
              lessons={sourceLessons.data}
              selectedLessonIds={selectableSourceLessonIds}
              anchorLessonId={lessonId}
              disabled={!canCreate || confirmOpen || create.isPending}
              onChange={(ids) => setSelectedLessonIds(normalizeSourceLessonIds(ids))}
            />
          ) : null}
          <Card size="sm" aria-labelledby={sourcePickerTitleId}>
            <CardHeader>
              <CardTitle id={sourcePickerTitleId}>{t('reviewNote.sourceLabel')}</CardTitle>
              <CardDescription>{t('reviewNote.createGuide')}</CardDescription>
            </CardHeader>
            <CardContent className="@container flex min-w-0 flex-col gap-3">
              <ToggleGroup
                variant="outline"
                value={selectedSource == null ? [] : [selectedSource]}
                onValueChange={(values) => setSelectedSource(SOURCE_MODES.find((mode) => mode === values[0]) ?? null)}
                aria-label={t('reviewNote.sourceLabel')}
                className="grid w-full grid-cols-1 gap-2 @sm:grid-cols-3"
              >
                {SOURCE_MODES.map((source) => {
                  const count = previewReady ? preview.data?.counts[source] : undefined
                  return (
                    <ToggleGroupItem
                      key={source}
                      value={source}
                      disabled={!canCreate || confirmOpen || create.isPending || usedSources.has(source) || !previewReady || count === 0}
                      aria-label={t(`reviewNote.source.${source}`)}
                      aria-describedby={count == null ? undefined : `${sourcePickerTitleId}-${source}-count`}
                      title={usedSources.has(source) ? t('reviewNote.sourceAlreadyUsed') : undefined}
                      className="h-auto min-h-18 min-w-0 justify-start gap-3 px-3 py-3"
                    >
                      <ReviewNoteSourceIcon source={source} selected={selectedSource === source} />
                      <span className="flex min-w-0 flex-1 flex-col items-start gap-1">
                        <span className="max-w-full break-words whitespace-normal text-left">{t(`reviewNote.source.${source}`)}</span>
                        <span id={`${sourcePickerTitleId}-${source}-count`} className="tabular-nums">{count == null ? '—' : t('reviewNote.questionCount', { count })}</span>
                      </span>
                    </ToggleGroupItem>
                  )
                })}
              </ToggleGroup>
              {usedSources.size > 0 ? <p className="text-xs text-muted-foreground">{t('reviewNote.sourceAlreadyUsed')}</p> : null}
              {hasAttemptScope && selectableSourceLessonIds.length === 0 ? (
                <Alert role="status"><AlertDescription>{t('reviewNote.emptySelection')}</AlertDescription></Alert>
              ) : preview.isFetching ? (
                <p role="status" className="text-sm text-muted-foreground">{t('reviewNote.previewLoading')}</p>
              ) : preview.error ? (
                <Alert variant="destructive">
                  <AlertDescription>{t('reviewNote.previewError')}</AlertDescription>
                  <Button variant="outline" size="sm" onClick={() => { void preview.refetch() }}>{t('reviewNote.retry')}</Button>
                </Alert>
              ) : previewReady && preview.data && (selectedQuestionCount === 0 || preview.data.counts.WRONG === 0) ? (
                <Alert role="status">
                  <AlertDescription>
                    {selectedQuestionCount === 0 ? <p>{t('reviewNote.noQuestions')}</p> : null}
                    {preview.data.counts.WRONG === 0 ? <p>{t('reviewNote.noWrongQuestions')}</p> : null}
                  </AlertDescription>
                </Alert>
              ) : null}
            </CardContent>
            <CardFooter className="flex-wrap justify-between gap-3">
              <p className="min-w-0 flex-1 break-words text-sm" aria-live="polite">
                {selectedSource == null
                  ? t('reviewNote.noSourceSelected')
                  : previewReady && selectedQuestionCount != null
                    ? t('reviewNote.rangeQuestionCount', { source: t(`reviewNote.source.${selectedSource}`), count: selectedQuestionCount })
                    : t(`reviewNote.source.${selectedSource}`)}
              </p>
              <Button type="button" className="shrink-0" disabled={!canSubmit} onClick={() => { setCreateWithExclusion(false); setConfirmOpen(true) }}>
                {create.isPending ? t('reviewNote.creating') : t('reviewNote.create')}
              </Button>
              {gradingMode === 'grading' ? (
                <Button type="button" variant="outline" className="shrink-0" disabled={!canSubmit} onClick={() => { setCreateWithExclusion(true); setConfirmOpen(true) }}>
                  {t('reviewNote.createAndExclude')}
                </Button>
              ) : null}
            </CardFooter>
          </Card>
        </div>
      ) : null}

      {selectedSetId == null ? null : detail.isLoading ? (
        <p role="status" className="p-6 text-sm text-gray-500">{t('reviewNote.detailLoading')}</p>
      ) : detail.error || activeReviewNote == null ? (
        <p role="alert" className="p-6 text-sm text-red-600">{t('reviewNote.detailError')}</p>
      ) : (
        <div>
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-4 py-3">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="font-semibold text-gray-900">{t(`reviewNote.source.${activeReviewNote.sourceMode}`)}</span>
              <span className="rounded-full bg-blue-50 px-2 py-1 text-xs font-semibold text-blue-700">
                {t('reviewNote.gradedProgress', {
                  graded: activeReviewNote.gradedCount,
                  total: activeReviewNote.totalCount,
                })}
              </span>
              {activeReviewNote.requiredForCompletion ? (
                <span className="rounded-full bg-amber-50 px-2 py-1 text-xs font-semibold text-amber-700">
                  {t('reviewNote.completionRequired')}
                </span>
              ) : null}
              {activeReviewNote.score != null ? (
                <span className="text-xs font-semibold text-gray-600">
                  {t('reviewNote.score', { score: activeReviewNote.score })}
                </span>
              ) : null}
            </div>
            {!readOnly ? (
              <div className="flex flex-wrap items-center justify-end gap-2">
                <button
                  type="button"
                  disabled={lockForPrint.isPending || exclude.isPending}
                  onClick={() => {
                    lockForPrint.mutate(undefined, {
                      onSuccess: () => {
                        window.open(
                          `/admin/coaching/print/${learnerId}/${lessonId}/review-note?setId=${activeReviewNote.setId}`,
                          '_blank',
                          'noopener,noreferrer',
                        )
                      },
                    })
                  }}
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {lockForPrint.isPending ? t('reviewNote.printing') : t('reviewNote.print')}
                </button>
                <LmsPrintPreviewPdfButton
                  disabled={lockForPrint.isPending || exclude.isPending}
                  onClick={() => {
                    lockForPrint.mutate(undefined, {
                      onSuccess: () => openLmsPrintPdfPreview(
                        `/admin/coaching/print/${learnerId}/${lessonId}/review-note?setId=${activeReviewNote.setId}`,
                      ),
                    })
                  }}
                />
                {activeReviewNote.status !== 'COMPLETED' ? (
                  <>
                    {activeReviewNote.canCancel ? (
                      <button
                        type="button"
                        disabled={exclude.isPending}
                        onClick={() => setCancellation({ setId: activeReviewNote.setId, reason: cancelReason })}
                        className="rounded-md border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-700"
                      >
                        {t('reviewNote.cancelSet')}
                      </button>
                    ) : null}
                    <button
                      type="button"
                      disabled={!canComplete || complete.isPending || exclude.isPending}
                      onClick={() => complete.mutate()}
                      className="rounded-md bg-primary-600 px-3 py-1.5 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {complete.isPending ? t('reviewNote.completing') : t('reviewNote.complete')}
                    </button>
                  </>
                ) : null}
              </div>
            ) : null}
          </div>
          {activeReviewNote.sourceLessons && activeReviewNote.sourceLessons.length > 0 ? (
            <div className="border-b border-border px-4 py-3">
              <ReviewNoteSourceSummary lessons={activeReviewNote.sourceLessons} totalCount={activeReviewNote.totalCount} sourceMode={activeReviewNote.sourceMode} />
            </div>
          ) : null}
          {showCancel && !readOnly && activeReviewNote.canCancel ? (
            <div className="space-y-2 border-b border-red-100 bg-red-50/50 px-4 py-3">
              <label className="block text-xs font-semibold text-red-800">
                {t('reviewNote.cancelReason')}
                <textarea
                  value={cancelReason}
                  onChange={(event) => setCancellation({ setId: activeReviewNote.setId, reason: event.target.value })}
                  className="mt-1 block min-h-20 w-full rounded-md border border-red-200 bg-white px-3 py-2 text-sm font-normal text-gray-900"
                />
              </label>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  disabled={cancel.isPending}
                  onClick={() => {
                    setCancellation(null)
                  }}
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700"
                >
                  {t('reviewNote.dialogCancel')}
                </button>
                <button
                  type="button"
                  disabled={cancelReason.trim().length === 0 || cancel.isPending || exclude.isPending}
                  onClick={() => {
                    if (cancel.isPending || exclude.isPending) return
                    cancel.mutate({ reason: cancelReason.trim() }, {
                      onSuccess: (cancelled) => setCancellation((current) => current?.setId === cancelled.setId ? null : current),
                    })
                  }}
                  className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {cancel.isPending ? t('reviewNote.cancelling') : t('reviewNote.confirmCancel')}
                </button>
              </div>
            </div>
          ) : null}
          {gradingMode === 'grading' ? (
            <div className="flex flex-col gap-4 p-4">
              {canExclude ? (
                <ReviewNoteExclusionList
                  key={activeReviewNote.setId}
                  reviewNote={activeReviewNote}
                  expanded={exclusionExpanded}
                  onExpandedChange={(expanded) => setExclusionSetId(expanded ? activeReviewNote.setId : null)}
                  disabled={exclusionDisabled}
                  isExcluding={exclude.isPending}
                  onExclude={excludeItem}
                />
              ) : null}
              <div hidden={hideGrading}>
                <ReviewNoteGradingSheet
                  learnerId={learnerId}
                  reviewNote={activeReviewNote}
                  canGrade={!readOnly && activeReviewNote.canGrade && !exclude.isPending}
                  onRequestCoaching={onRequestCoaching}
                  onComplete={readOnly || preventFinalization ? undefined : () => complete.mutate()}
                  completing={complete.isPending}
                />
              </div>
            </div>
          ) : (
            <QuestionGradingList
              key={activeReviewNote.setId}
              presentation="single"
              items={activeReviewNote.items.map(toQuestionGradingItem)}
              canGrade={!readOnly && activeReviewNote.canGrade && !exclude.isPending}
              isSubmitting={grade.isPending || exclude.isPending}
              emptyMessage={t('reviewNote.empty')}
              initialSelectedQuizId={focusRequest?.quizId ?? null}
              focusRequest={focusRequest}
              renderItemActions={canExclude ? (question, index) => {
                const item = activeReviewNote.items.find((candidate) => candidate.quizId === question.quizId)
                if (item == null) return null
                return <ReviewNoteExcludeButton
                  questionNumber={index + 1}
                  minimumReached={activeReviewNote.totalCount <= 1 || activeReviewNote.items.length <= 1}
                  disabled={exclusionDisabled}
                  pending={exclude.isPending && exclude.variables?.itemId === item.itemId}
                  onClick={() => excludeItem(item)}
                />
              } : undefined}
              onGrade={({ quizId, answers, isCorrect, manualGrade }) => {
                return grade.mutateAsync({
                  quizId,
                  answer: JSON.stringify(answers),
                  isCorrect,
                  manualGrade,
                })
              }}
            />
          )}
        </div>
      )}

      {LocalAnswerPreview && (window.location.hostname === 'localhost' || window.location.hostname.endsWith('.localhost')) ? (
        <Suspense fallback={null}><LocalAnswerPreview /></Suspense>
      ) : null}

      <ConfirmDialog
        open={confirmOpen}
        title={t('reviewNote.createConfirmTitle')}
        message={t('reviewNote.createConfirmMessage')}
        confirmLabel={t('reviewNote.createConfirm')}
        cancelLabel={t('reviewNote.dialogCancel')}
        pending={create.isPending || confirming}
        confirmDisabled={!canSubmit}
        tone="primary"
        variant="warning"
        onCancel={() => setConfirmOpen(false)}
        onConfirm={async () => {
          if (!canSubmit || selectedSource == null) return
          const sourceLessonIds = normalizeSourceLessonIds(selectableSourceLessonIds)
          setConfirming(true)
          try {
            const fresh = await preview.refetch({ throwOnError: true })
            if (!mounted.current || !isReviewNotePreviewForSelection(fresh.data, sourceLessonIds) || !(fresh.data.counts[selectedSource] > 0)) return
            const latestLessons = queryClient.getQueryData<ReviewNoteSourceLessonView[]>(
              reviewNoteSourceKeys.lessons(learnerId, lessonId, learningAttemptId),
            )
            if (latestLessons == null || filterSelectableSourceLessonIds(sourceLessonIds, latestLessons).length !== sourceLessonIds.length) {
              setConfirmOpen(false)
              return
            }
            const reviewNote = await create.mutateAsync({ sourceMode: selectedSource, learningAttemptId, sourceLessonIds })
            if (!mounted.current) return
            setCancellation(null)
            setRequestedSetId(reviewNote.setId)
            setExclusionSetId(createWithExclusion ? reviewNote.setId : null)
            setSelectedSource(null)
            setConfirmOpen(false)
          } catch {
            // Close the dialog so the query or mutation error and retry controls are visible.
            if (mounted.current) setConfirmOpen(false)
          } finally {
            if (mounted.current) setConfirming(false)
          }
        }}
      >
        {confirming ? <p role="status" className="text-sm text-muted-foreground">{create.isPending ? t('reviewNote.creating') : t('reviewNote.previewLoading')}</p> : null}
        {previewReady && preview.data ? <ReviewNoteSourceSummary lessons={preview.data.sourceLessons} totalCount={selectedQuestionCount} sourceMode={selectedSource ?? undefined} /> : null}
        {preview.error ? <p role="alert">{t('reviewNote.previewError')}</p> : null}
      </ConfirmDialog>
    </CoachingOperationPanel>
  )
}
